import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '@/components/base/Card';
import StatusBadge from '@/components/base/StatusBadge';
import Badge from '@/components/base/Badge';
import Modal from '@/components/base/Modal';
import EmptyState from '@/components/base/EmptyState';
import { useData } from '@/context/DataContext';
import { formatTL, formatDate } from '@/lib/format';
import { useApp } from '@/context/AppContext';

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useApp();
  const { quotes, customerName, customerById } = useData();
  const quote = quotes.find((q) => q.id === id);
  const [convertOpen, setConvertOpen] = useState(false);

  if (!quote) {
    return (
      <div className="animate-fade-in-up">
        <EmptyState icon="ri-file-search-line" title="Teklif bulunamadı" description="Aradığınız kayıt mevcut değil." />
        <div className="mt-4 text-center">
          <button type="button" onClick={() => navigate('/quotes')} className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600">
            Teklif Listesine Dön
          </button>
        </div>
      </div>
    );
  }

  const margin = quote.amount > 0 ? Math.round((quote.estimatedProfit / quote.amount) * 100) : 0;
  const customer = customerById(quote.customerId);

  const convertToProject = () => {
    setConvertOpen(false);
    showToast('Teklif projeye dönüştürüldü');
    navigate('/projects');
  };

  const history = [
    { date: quote.date, text: 'Teklif oluşturuldu', icon: 'ri-file-add-line' },
    { date: quote.lastContact, text: `Son görüşme yapıldı · Durum: ${quote.status}`, icon: 'ri-chat-3-line' },
    { date: quote.validityDate, text: 'Geçerlilik süresi sonu', icon: 'ri-calendar-check-line' },
  ];

  return (
    <div className="space-y-5 animate-fade-in-up">
      <button type="button" onClick={() => navigate('/quotes')} className="flex cursor-pointer items-center gap-1 text-sm font-semibold text-foreground-500 transition-colors hover:text-foreground-900">
        <i className="ri-arrow-left-line" /> Teklifler
      </button>

      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-xl font-semibold text-foreground-950">{quote.projectName}</h1>
              <StatusBadge status={quote.status} />
            </div>
            <p className="mt-1 text-sm text-foreground-500">{quote.number} · {quote.category}</p>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5"><i className="ri-team-line" /> {customerName(quote.customerId)}</span>
              <span className="flex items-center gap-1.5"><i className="ri-calendar-line" /> Teklif: {formatDate(quote.date)}</span>
              <span className="flex items-center gap-1.5"><i className="ri-timer-line" /> Geçerlilik: {formatDate(quote.validityDate)}</span>
            </div>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <p className="font-heading text-3xl font-semibold text-foreground-950">{formatTL(quote.amount)}</p>
            <button
              type="button"
              onClick={() => setConvertOpen(true)}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-600"
            >
              <i className="ri-swap-line" /> Teklifi Projeye Dönüştür
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card className="p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-950">Teklif Detayları</h2>
            <dl className="mt-4 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
              <div><dt className="text-foreground-500">Talep Açıklaması</dt><dd className="mt-1 text-foreground-800">{quote.description}</dd></div>
              <div><dt className="text-foreground-500">Teknik Açıklamalar</dt><dd className="mt-1 text-foreground-800">{quote.technicalNotes}</dd></div>
              <div><dt className="text-foreground-500">Vergi Durumu</dt><dd className="mt-1 font-medium text-foreground-900">{quote.taxStatus}</dd></div>
              <div><dt className="text-foreground-500">Teslim Süresi</dt><dd className="mt-1 font-medium text-foreground-900">{quote.deliveryTime}</dd></div>
              <div className="sm:col-span-2"><dt className="text-foreground-500">Ödeme Planı</dt><dd className="mt-1 font-medium text-foreground-900">{quote.paymentPlan}</dd></div>
              {quote.internalNote && (
                <div className="sm:col-span-2"><dt className="text-foreground-500">İç Not</dt><dd className="mt-1 rounded-md bg-background-100 p-3 text-foreground-700">{quote.internalNote}</dd></div>
              )}
            </dl>
          </Card>

          <Card className="p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-950">Teklif Geçmişi</h2>
            <div className="relative mt-4 border-l-2 border-background-200 pl-6">
              {history.map((h, i) => (
                <div key={i} className="relative pb-6 last:pb-0">
                  <span className="absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full bg-background-50 text-foreground-400 ring-1 ring-background-200">
                    <i className={`${h.icon} text-xs`} />
                  </span>
                  <p className="text-sm font-medium text-foreground-900">{h.text}</p>
                  <p className="text-xs text-foreground-500">{formatDate(h.date)}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <Card className="p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-950">Finansal Özet</h2>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-foreground-500">Teklif Tutarı</span><span className="font-semibold text-foreground-900">{formatTL(quote.amount)}</span></div>
              <div className="flex justify-between"><span className="text-foreground-500">Tahmini Maliyet</span><span className="font-semibold text-foreground-900">{formatTL(quote.estimatedCost)}</span></div>
              <div className="flex justify-between"><span className="text-foreground-500">Tahmini Kâr</span><span className="font-semibold text-primary-700">{formatTL(quote.estimatedProfit)}</span></div>
              <div className="flex justify-between border-t border-background-200 pt-3"><span className="text-foreground-500">Kâr Marjı</span><Badge tone="accent">%{margin}</Badge></div>
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-950">Müşteri</h2>
            {customer && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-foreground-900">{customer.name}</p>
                <p className="mt-1 text-sm text-foreground-600">{customer.contactPerson}</p>
                <p className="text-sm text-foreground-600">{customer.phone}</p>
                <p className="text-sm text-foreground-600">{customer.email}</p>
              </div>
            )}
          </Card>
        </div>
      </div>

      <Modal
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        title="Teklifi Projeye Dönüştür"
        subtitle="Teklif bilgileri yeni projeye aktarılacak"
      >
        <div className="space-y-4">
          <div className="rounded-md bg-primary-100/70 p-4 text-sm">
            <div className="flex justify-between"><span className="text-foreground-500">Proje Adı</span><span className="font-semibold text-foreground-900">{quote.projectName}</span></div>
            <div className="mt-2 flex justify-between"><span className="text-foreground-500">Müşteri</span><span className="font-semibold text-foreground-900">{customerName(quote.customerId)}</span></div>
            <div className="mt-2 flex justify-between"><span className="text-foreground-500">Kategori</span><span className="font-semibold text-foreground-900">{quote.category}</span></div>
            <div className="mt-2 flex justify-between"><span className="text-foreground-500">Satış Tutarı</span><span className="font-semibold text-foreground-900">{formatTL(quote.amount)}</span></div>
          </div>
          <p className="text-sm text-foreground-500">Yeni proje, ilk aşama olarak "Proje Alındı" durumunda oluşturulacaktır.</p>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setConvertOpen(false)} className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100">Vazgeç</button>
            <button type="button" onClick={convertToProject} className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600">Projeyi Oluştur</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}